import { useState, useEffect, useCallback } from "react";

const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbz-DG4ALQCzg71hMg5acF_86USEb8ltIyGrgo3dQiCZAbKhvXOGudrVA1DZbHCwGekB/exec";
const AUTH_TOKEN = "garrett-2026";

const api = {
  get: (action, params = {}) => {
    const qs = new URLSearchParams({ action, token: AUTH_TOKEN, ...params }).toString();
    return fetch(`${SCRIPT_URL}?${qs}`).then(r => r.json());
  },
  post: (body) =>
    fetch(SCRIPT_URL, {
      method: "POST",
      body: JSON.stringify({ ...body, token: AUTH_TOKEN }),
    }).then(r => r.json()),
};

// Status colors — NOT_INTERESTED and NO_ANSWER are neutral/colorless
const STATUS_META = {
  READY:          { bg: "rgba(34,197,94,0.12)",   color: "#4ade80",  label: "Ready" },
  IN_PROGRESS:    { bg: "rgba(20,184,166,0.12)",  color: "#2dd4bf",  label: "In Progress" },
  BOOKED:         { bg: "rgba(22,163,74,0.15)",   color: "#16a34a",  label: "Booked" },
  CALLBACK:       { bg: "rgba(234,179,8,0.15)",   color: "#eab308",  label: "Callback" },
  THINKING:       { bg: "rgba(100,116,139,0.15)", color: "#94a3b8",  label: "Thinking" },
  NOT_INTERESTED: { bg: "rgba(148,163,184,0.08)", color: "#64748b",  label: "Not Interested" },
  DNC:            { bg: "rgba(239,68,68,0.12)",   color: "#f87171",  label: "DNC" },
  ERROR:          { bg: "rgba(153,27,27,0.15)",   color: "#fca5a5",  label: "Error" },
  NO_ANSWER:      { bg: "rgba(148,163,184,0.08)", color: "#64748b",  label: "No Answer" },
  WRONG_NUMBER:   { bg: "rgba(167,139,250,0.12)", color: "#c4b5fd",  label: "Wrong Number" },
  EXHAUSTED:      { bg: "rgba(239,68,68,0.12)",   color: "#f87171",  label: "Exhausted" },
  REVIEW:         { bg: "rgba(56,189,248,0.12)",  color: "#7dd3fc",  label: "Review" },
};

const INTEREST_META = {
  HIGH:   { bg: "rgba(34,197,94,0.12)",   color: "#4ade80" },
  MEDIUM: { bg: "rgba(234,179,8,0.15)",   color: "#eab308" },
  LOW:    { bg: "rgba(148,163,184,0.08)", color: "#64748b" },
};

const ALL_STATUSES = ["READY","IN_PROGRESS","BOOKED","CALLBACK","THINKING","NOT_INTERESTED","DNC","ERROR","NO_ANSWER","WRONG_NUMBER","EXHAUSTED","REVIEW"];
const ALL_INTERESTS = ["HIGH","MEDIUM","LOW"];
const FILTER_STATUSES = ["all","READY","BOOKED","CALLBACK","NOT_INTERESTED","DNC","NO_ANSWER","WRONG_NUMBER","REVIEW"];

function Badge({ type, value }) {
  const meta = type === "interest" ? INTEREST_META[value] : STATUS_META[value];
  if (!meta || !value) return <span style={{ color: "#475569", fontSize: 11 }}>—</span>;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 500,
      padding: "3px 8px", borderRadius: 10, background: meta.bg, color: meta.color, whiteSpace: "nowrap"
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
      {type === "status" ? (STATUS_META[value]?.label || value) : value}
    </span>
  );
}

function Btn({ label, onClick, variant = "default", small, disabled }) {
  const [hov, setHov] = useState(false);
  const styles = {
    default: { border: hov ? "#00B4FF" : "#2d3748", color: hov ? "#00B4FF" : "#94a3b8", bg: hov ? "rgba(0,180,255,0.06)" : "transparent" },
    success: { border: hov ? "#4ade80" : "#2d3748", color: hov ? "#4ade80" : "#94a3b8", bg: hov ? "rgba(74,222,128,0.06)" : "transparent" },
    danger:  { border: hov ? "#f87171" : "#2d3748", color: hov ? "#f87171" : "#94a3b8", bg: hov ? "rgba(248,113,113,0.06)" : "transparent" },
    primary: { border: "rgba(0,180,255,0.4)",        color: "#00B4FF",                   bg: "rgba(0,180,255,0.1)" },
  };
  const s = styles[variant];
  return (
    <button disabled={disabled} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        fontSize: small ? 11 : 12, padding: small ? "3px 9px" : "6px 14px",
        borderRadius: 6, cursor: "pointer", border: `0.5px solid ${s.border}`,
        background: s.bg, color: s.color, marginRight: 4, opacity: disabled ? 0.4 : 1
      }}>{label}</button>
  );
}

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#111827", border: "0.5px solid #1f2937", borderRadius: 12, padding: 28, width: 420, maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: "#e2e8f0", marginBottom: 20 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || ""}
      style={{ width: "100%", background: "#0f172a", border: "0.5px solid #1f2937", borderRadius: 6, padding: "8px 12px", fontSize: 13, color: "#cbd5e1", boxSizing: "border-box" }} />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: "100%", background: "#0f172a", border: "0.5px solid #1f2937", borderRadius: 6, padding: "8px 12px", fontSize: 13, color: "#cbd5e1", boxSizing: "border-box" }}>
      <option value="">— select —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function StatCard({ label, value, accent, sub }) {
  return (
    <div style={{ background: accent ? "rgba(0,180,255,0.04)" : "#111827", border: `0.5px solid ${accent ? "rgba(0,180,255,0.25)" : "#1f2937"}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 11, color: "#475569", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 500, color: accent ? "#00B4FF" : "#e2e8f0", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function LeadFormModal({ open, onClose, onSave, initial }) {
  const empty = { number: "", name: "", email: "", company: "", lead_tag: "", status: "READY", interest_level: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial ? { ...empty, ...initial } : empty); }, [initial, open]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.number || !form.name) return alert("Name and phone are required");
    setSaving(true);
    await onSave(form);
    setSaving(false);
    onClose();
  };

  return (
    <Modal open={open} title={initial ? "Edit lead" : "Add lead"} onClose={onClose}>
      <Field label="Full name *"><Input value={form.name} onChange={set("name")} placeholder="John Smith" /></Field>
      <Field label="Phone number *"><Input value={form.number} onChange={set("number")} placeholder="512 555 0100" /></Field>
      <Field label="Email"><Input value={form.email} onChange={set("email")} placeholder="john@email.com" /></Field>
      <Field label="Company"><Input value={form.company} onChange={set("company")} /></Field>
      <Field label="Tag"><Input value={form.lead_tag} onChange={set("lead_tag")} placeholder="e.g. referral" /></Field>
      <Field label="Status"><Select value={form.status} onChange={set("status")} options={ALL_STATUSES} /></Field>
      <Field label="Interest level"><Select value={form.interest_level} onChange={set("interest_level")} options={ALL_INTERESTS} /></Field>
      <Field label="Notes"><Input value={form.notes} onChange={set("notes")} placeholder="Any context..." /></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <Btn label="Cancel" onClick={onClose} />
        <Btn label={saving ? "Saving..." : "Save"} variant="primary" onClick={save} disabled={saving} />
      </div>
    </Modal>
  );
}

function ConfirmModal({ open, title, sub, onClose, onConfirm, variant }) {
  const [loading, setLoading] = useState(false);
  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 24 }}>{sub}</div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn label="Cancel" onClick={onClose} />
        <Btn label={loading ? "..." : "Confirm"} variant={variant || "danger"} onClick={async () => { setLoading(true); await onConfirm(); setLoading(false); }} />
      </div>
    </Modal>
  );
}

function NoteModal({ open, title, sub, onClose, onConfirm, variant }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>{sub}</div>
      <Field label="Notes (optional)"><Input value={note} onChange={setNote} placeholder="Optional notes..." /></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <Btn label="Cancel" onClick={onClose} />
        <Btn label={loading ? "..." : "Confirm"} variant={variant || "danger"} onClick={async () => { setLoading(true); await onConfirm(note); setLoading(false); }} />
      </div>
    </Modal>
  );
}

function CallModal({ open, lead, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  return (
    <Modal open={open} title="Trigger call" onClose={onClose}>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>Send Kyle to call {lead?.name} now.</div>
      <Field label="Note for Kyle (optional)"><Input value={note} onChange={setNote} placeholder="e.g. Lead asked about retirement..." /></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <Btn label="Cancel" onClick={onClose} />
        <Btn label={loading ? "Sending..." : "Send Kyle"} variant="primary" onClick={async () => { setLoading(true); await onConfirm(note); setLoading(false); }} />
      </div>
    </Modal>
  );
}

function LoginPage({ onLogin }) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const handle = () => {
    if (user === "garrett" && pass === "kyle2026") onLogin();
    else setErr("Invalid credentials");
  };
  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1e", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#111827", border: "0.5px solid #1f2937", borderRadius: 16, padding: 40, width: 340 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <polygon points="14,3 26,24 2,24" fill="none" stroke="#00B4FF" strokeWidth="1.5"/>
            <polygon points="14,8 22,22 6,22" fill="none" stroke="#0066CC" strokeWidth="1"/>
          </svg>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#f1f5f9" }}>DeltaSuite</div>
            <div style={{ fontSize: 11, color: "#00B4FF" }}>Kyle Portal</div>
          </div>
        </div>
        <Field label="Username"><Input value={user} onChange={setUser} /></Field>
        <Field label="Password">
          <input type="password" value={pass} onChange={e => setPass(e.target.value)} onKeyDown={e => e.key === "Enter" && handle()}
            style={{ width: "100%", background: "#0f172a", border: "0.5px solid #1f2937", borderRadius: 6, padding: "8px 12px", fontSize: 13, color: "#cbd5e1", boxSizing: "border-box" }} />
        </Field>
        {err && <div style={{ fontSize: 12, color: "#f87171", marginBottom: 12 }}>{err}</div>}
        <button onClick={handle} style={{ width: "100%", padding: 10, borderRadius: 8, cursor: "pointer", background: "rgba(0,180,255,0.12)", border: "0.5px solid rgba(0,180,255,0.3)", color: "#00B4FF", fontSize: 13, fontWeight: 500, marginTop: 8 }}>Sign in</button>
      </div>
    </div>
  );
}

export default function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("all").then(d => { setData(d); setLoading(false); });
  }, []);


  useEffect(() => { if (loggedIn) load(); }, [loggedIn, load]);

  if (!loggedIn) return <LoginPage onLogin={() => setLoggedIn(true)} />;

  const s = data?.stats;
  const leads = data?.leads?.leads || [];
  const booked = data?.booked?.booked || [];
  const callbacks = data?.callbacks?.callbacks || [];
  const filteredLeads = statusFilter === "all" ? leads : leads.filter(l => l.status === statusFilter);

  const closeModal = () => setModal(null);

  const navItems = [
    { id: "overview",  label: "Dashboard" },
    { id: "leads",     label: "All Leads" },
    { id: "booked",    label: "Booked" },
    { id: "callbacks", label: "Callbacks" },
  ];

  const TH = ({ children }) => (
    <th style={{ textAlign: "left", fontSize: 10, color: "#475569", fontWeight: 500, letterSpacing: "0.5px", textTransform: "uppercase", padding: "10px 16px", borderBottom: "0.5px solid #1f2937", background: "#0d1117", whiteSpace: "nowrap" }}>{children}</th>
  );
  const TD = ({ children, mono, muted, blue, nowrap }) => (
    <td style={{ padding: "10px 16px", color: mono ? "#475569" : muted ? "#475569" : blue ? "#38bdf8" : "#cbd5e1", fontFamily: mono ? "monospace" : undefined, fontSize: mono ? 11 : 12, borderBottom: "0.5px solid #0d1117", whiteSpace: nowrap ? "nowrap" : undefined }}>{children}</td>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#0a0f1e", color: "#cbd5e1", fontFamily: "system-ui,sans-serif" }}>

      {/* SIDEBAR */}
      <div style={{ width: 220, minWidth: 220, background: "#0d1117", borderRight: "0.5px solid #1f2937", display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0 }}>
        <div style={{ padding: "20px 20px 20px", borderBottom: "0.5px solid #1f2937" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
              <polygon points="14,3 26,24 2,24" fill="none" stroke="#00B4FF" strokeWidth="1.5"/>
              <polygon points="14,8 22,22 6,22" fill="none" stroke="#0066CC" strokeWidth="1"/>
            </svg>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#f1f5f9" }}>DeltaSuite</div>
              <div style={{ fontSize: 10, color: "#00B4FF" }}>Kyle Portal</div>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: "12px 0", overflowY: "auto" }}>
          <div style={{ fontSize: 10, color: "#334155", letterSpacing: "0.8px", textTransform: "uppercase", padding: "10px 18px 4px", fontWeight: 500 }}>Navigation</div>
          {navItems.map(n => (
            <div key={n.id} onClick={() => setTab(n.id)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "9px 18px",
              fontSize: 13, cursor: "pointer",
              color: tab === n.id ? "#38bdf8" : "#64748b",
              background: tab === n.id ? "rgba(56,189,248,0.06)" : "transparent",
              borderLeft: `2px solid ${tab === n.id ? "#38bdf8" : "transparent"}`,
            }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", opacity: 0.7 }} />
              {n.label}
            </div>
          ))}
        </div>

        {/* STICKY AGENT STATUS */}
        <div style={{ padding: "14px 18px", borderTop: "0.5px solid #1f2937", background: "#0d1117" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ade80", boxShadow: "0 0 6px #4ade80", flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 10, color: "#334155" }}>Agent status</div>
              <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>Kyle — Active</div>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* TOPBAR */}
        <div style={{ background: "#0d1117", borderBottom: "0.5px solid #1f2937", padding: "13px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#e2e8f0" }}>{navItems.find(n => n.id === tab)?.label}</div>
            <div style={{ fontSize: 11, color: "#334155" }}>{new Date().toDateString()}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Btn label="↻ Refresh" onClick={load} small />
            {tab === "leads" && <Btn label="+ Add lead" variant="primary" onClick={() => setAddOpen(true)} small />}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#111827", border: "0.5px solid #1f2937", borderRadius: 20, padding: "5px 12px 5px 6px" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(0,180,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 500, color: "#00B4FF" }}>GW</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>Garrett W.</div>
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div style={{ padding: "22px 24px", flex: 1, overflowY: "auto" }}>
          {loading ? (
            <div style={{ color: "#334155", fontSize: 13 }}>Loading data...</div>
          ) : (<>

            {/* OVERVIEW */}
            {tab === "overview" && (<>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 12, marginBottom: 22 }}>
                <StatCard label="Booked today"   value={s?.today?.booked ?? 0}       accent />
                <StatCard label="Calls today"    value={s?.today?.calls ?? 0}        sub="total" />
                <StatCard label="Callbacks"      value={s?.today?.callback ?? 0}     sub="pending" />
                <StatCard label="Not interested" value={s?.today?.notInterested ?? 0} sub="today" />
                <StatCard label="Book rate"      value={`${s?.today?.bookRate ?? 0}%`} sub={`${s?.today?.calls ?? 0} calls`} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 22 }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>Upcoming appointments</div>
                    <span onClick={() => setTab("booked")} style={{ fontSize: 11, color: "#334155", cursor: "pointer", padding: "4px 10px", background: "#111827", border: "0.5px solid #1f2937", borderRadius: 6 }}>View all</span>
                  </div>
                  <div style={{ background: "#111827", border: "0.5px solid #1f2937", borderRadius: 10, overflow: "hidden" }}>
                    {booked.slice(0, 3).map((b, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: i < 2 ? "0.5px solid #0d1117" : "none" }}>
                        <div>
                          <div style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 500 }}>{b.name}</div>
                          <div style={{ fontSize: 11, color: "#334155" }}>{b.phone}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 12, color: "#38bdf8" }}>{b.datetime}</div>
                          <div style={{ fontSize: 10, color: "#334155" }}>{b.method}</div>
                        </div>
                        <div style={{ display: "flex", gap: 4, marginLeft: 10 }}>
                          <Btn label="Showed" variant="success" small onClick={() => setModal({ type: "showed", lead: b })} />
                          <Btn label="No-show" variant="danger" small onClick={() => setModal({ type: "noshow", lead: b })} />
                        </div>
                      </div>
                    ))}
                    {booked.length === 0 && <div style={{ padding: "16px", fontSize: 12, color: "#334155" }}>No upcoming appointments</div>}
                  </div>
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>Callback queue</div>
                    <span onClick={() => setTab("callbacks")} style={{ fontSize: 11, color: "#334155", cursor: "pointer", padding: "4px 10px", background: "#111827", border: "0.5px solid #1f2937", borderRadius: 6 }}>View all</span>
                  </div>
                  <div style={{ background: "#111827", border: "0.5px solid #1f2937", borderRadius: 10, overflow: "hidden" }}>
                    {callbacks.slice(0, 3).map((c, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: i < 2 ? "0.5px solid #0d1117" : "none", gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 500 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: "#eab308", marginTop: 2 }}>{c.callback_datetime || "No date"}</div>
                          <div style={{ fontSize: 11, color: "#334155", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.notes}</div>
                        </div>
                        <Btn label="Call now" variant="primary" small onClick={() => setModal({ type: "call", lead: c })} />
                      </div>
                    ))}
                    {callbacks.length === 0 && <div style={{ padding: "16px", fontSize: 12, color: "#334155" }}>No callbacks pending</div>}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 500, color: "#64748b", marginBottom: 12 }}>Recent calls</div>
              <LeadsTable leads={leads.slice(0, 10)} TH={TH} TD={TD} onEdit={setEditLead} onModal={setModal} />
            </>)}

            {/* LEADS */}
            {tab === "leads" && (<>
              <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
                {FILTER_STATUSES.map(f => (
                  <div key={f} onClick={() => setStatusFilter(f)} style={{
                    fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
                    border: `0.5px solid ${statusFilter === f ? "rgba(56,189,248,0.35)" : "#1f2937"}`,
                    color: statusFilter === f ? "#38bdf8" : "#475569",
                    background: statusFilter === f ? "rgba(56,189,248,0.06)" : "#111827"
                  }}>{f === "all" ? "All" : STATUS_META[f]?.label || f}</div>
                ))}
              </div>
              <LeadsTable leads={filteredLeads} TH={TH} TD={TD} onEdit={setEditLead} onModal={setModal} />
            </>)}

            {/* BOOKED */}
            {tab === "booked" && (
              <div style={{ background: "#111827", border: "0.5px solid #1f2937", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    {["Name","Phone","Date & Time","Method","Showed","Actions"].map(h => <TH key={h}>{h}</TH>)}
                  </tr></thead>
                  <tbody>
                    {booked.map((b, i) => (
                      <tr key={i}>
                        <TD>{b.name}</TD>
                        <TD mono>{b.phone}</TD>
                        <TD blue>{b.datetime}</TD>
                        <TD muted>{b.method}</TD>
                        <TD><Badge type="status" value={b.showed === "YES" ? "BOOKED" : b.showed === "NO_SHOW" ? "NOT_INTERESTED" : "IN_PROGRESS"} /></TD>
                        <TD nowrap>
                          <Btn label="Showed" variant="success" small onClick={() => setModal({ type: "showed", lead: b })} />
                          <Btn label="No-show" variant="danger" small onClick={() => setModal({ type: "noshow", lead: b })} />
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {booked.length === 0 && <div style={{ padding: 20, fontSize: 12, color: "#334155" }}>No booked appointments yet</div>}
              </div>
            )}

            {/* CALLBACKS */}
            {tab === "callbacks" && (
              <div style={{ background: "#111827", border: "0.5px solid #1f2937", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    {["Name","Phone","Due Date","Interest","Notes","Actions"].map(h => <TH key={h}>{h}</TH>)}
                  </tr></thead>
                  <tbody>
                    {callbacks.map((c, i) => (
                      <tr key={i}>
                        <TD>{c.name}</TD>
                        <TD mono>{c.formattedNumber}</TD>
                        <TD><span style={{ color: "#eab308", fontSize: 12 }}>{c.callback_datetime || "—"}</span></TD>
                        <TD><Badge type="interest" value={c.interest_level} /></TD>
                        <TD muted>{c.notes || "—"}</TD>
                        <TD nowrap>
                          <Btn label="Call now" variant="primary" small onClick={() => setModal({ type: "call", lead: c })} />
                          <Btn label="DNC" variant="danger" small onClick={() => setModal({ type: "dnc", lead: c })} />
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {callbacks.length === 0 && <div style={{ padding: 20, fontSize: 12, color: "#334155" }}>No callbacks pending</div>}
              </div>
            )}

          </>)}
        </div>
      </div>

      {/* ADD / EDIT LEAD MODAL */}
      <LeadFormModal open={addOpen} onClose={() => setAddOpen(false)}
        onSave={async form => { await api.post({ action: "addLead", ...form }); load(); }} />

      <LeadFormModal open={!!editLead} initial={editLead} onClose={() => setEditLead(null)}
        onSave={async form => { await api.post({ action: "editLead", rowIndex: editLead.rowIndex, ...form }); load(); }} />

      {/* ACTION MODALS */}
      <ConfirmModal open={modal?.type === "showed"} title="Mark as showed"
        sub={`${modal?.lead?.name} attended the meeting.`} variant="primary"
        onClose={closeModal} onConfirm={async () => { await api.post({ action: "markShowed", rowIndex: modal.lead.rowIndex, phone: modal.lead.phone }); closeModal(); load(); }} />

      <NoteModal open={modal?.type === "noshow"} title="Mark as no-show"
        sub={`${modal?.lead?.name} did not attend.`} variant="danger"
        onClose={closeModal} onConfirm={async note => { await api.post({ action: "markNoShow", rowIndex: modal.lead.rowIndex, phone: modal.lead.phone, notes: note }); closeModal(); load(); }} />

      <ConfirmModal open={modal?.type === "dnc"} title="Add to do not call"
        sub={`This will permanently block ${modal?.lead?.name} from being called.`} variant="danger"
        onClose={closeModal} onConfirm={async () => { await api.post({ action: "markDNC", phone: modal.lead.formattedNumber || modal.lead.phone, number: modal.lead.number }); closeModal(); load(); }} />

      <ConfirmModal open={modal?.type === "delete"} title="Delete lead"
        sub={`Permanently delete ${modal?.lead?.name} from the sheet? This cannot be undone.`} variant="danger"
        onClose={closeModal} onConfirm={async () => { await api.post({ action: "deleteLead", rowIndex: modal.lead.rowIndex }); closeModal(); load(); }} />

      <CallModal open={modal?.type === "call"} lead={modal?.lead} onClose={closeModal}
        onConfirm={async () => { await api.post({ action: "triggerCall", phone: modal.lead.formattedNumber || modal.lead.phone, name: modal.lead.name || modal.lead.firstName, number: modal.lead.number }); closeModal(); load(); }} />
    </div>
  );
}

function LeadsTable({ leads, TH, TD, onEdit, onModal }) {
  return (
    <div style={{ background: "#111827", border: "0.5px solid #1f2937", borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          {["Name","Phone","Status","Interest","Attempts","Notes","Actions"].map(h => <TH key={h}>{h}</TH>)}
        </tr></thead>
        <tbody>
          {leads.map((l, i) => (
            <tr key={i}>
              <TD><span style={{ color: "#e2e8f0", fontWeight: 500 }}>{l.name}</span></TD>
              <TD mono>{l.formattedNumber || l.number}</TD>
              <TD><Badge type="status" value={l.status} /></TD>
              <TD><Badge type="interest" value={l.interest_level} /></TD>
              <TD muted>{l.attempts}</TD>
              <TD muted>{l.notes ? l.notes.substring(0, 50) + (l.notes.length > 50 ? "…" : "") : "—"}</TD>
              <TD nowrap>
                <Btn label="Edit" small onClick={() => onEdit(l)} />
                {l.status !== "DNC" && <Btn label="Call" small onClick={() => onModal({ type: "call", lead: l })} />}
                {l.status !== "DNC" && <Btn label="DNC" variant="danger" small onClick={() => onModal({ type: "dnc", lead: l })} />}
                <Btn label="Delete" variant="danger" small onClick={() => onModal({ type: "delete", lead: l })} />
              </TD>
            </tr>
          ))}
        </tbody>
      </table>
      {leads.length === 0 && <div style={{ padding: 20, fontSize: 12, color: "#334155" }}>No leads found</div>}
    </div>
  );
}